import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputMaskModule } from 'primeng/inputmask';
import { SliderModule } from 'primeng/slider';

import {
  VideoClipFormControl,
  VideoClipFormValue,
  VideoProjectFormService,
} from '../../video-project.form';
import { VideoShotAssetComponent } from '../../video-shot-asset/video-shot-asset.component';
import {
  parseEndAt,
  parseStartAt,
  parseStringValue,
} from '../video-clip.utils';

export interface VideoClipEditorData {
  clip: FormGroup<VideoClipFormControl>;
  hasChanges: Signal<boolean>;
}

@Component({
  selector: 'app-video-clip-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FloatLabelModule,
    SliderModule,
    InputMaskModule,
    VideoShotAssetComponent,
  ],
  templateUrl: './video-clip-editor.component.html',
  styleUrl: './video-clip-editor.component.scss',
})
export class VideoClipEditorComponent implements OnInit {
  get clip() {
    return this.config.data?.clip;
  }

  private _startAt = '';

  get startAt(): string {
    return this._startAt || parseStartAt(this.clip.value.startAt);
  }

  set startAt(value: string) {
    try {
      const startAtMs = parseStringValue(value);
      if (Number.isFinite(startAtMs)) {
        const currentEndAt = this.clip.value.endAt;
        const maxAllowed = Math.max(currentEndAt, this.duration);
        const clampedStartAt = Math.max(0, Math.min(startAtMs, maxAllowed));
        this.range = [(clampedStartAt / this.duration) * 100, this.range[1]];
        this._startAt = parseStartAt(clampedStartAt);
        this.clip.patchValue({ startAt: clampedStartAt });
      } else {
        this._startAt = value;
      }
    } catch {
      this._startAt = value;
    }
  }

  private _endAt = '';

  get endAt(): string {
    return this._endAt || parseEndAt(this.clip.value.endAt, this.duration);
  }

  set endAt(value: string) {
    try {
      const endAtMs = parseStringValue(value);
      if (Number.isFinite(endAtMs)) {
        const currentStartAt = this.clip.value.startAt;
        const clampedEndAt = Math.max(
          currentStartAt,
          Math.min(endAtMs, this.duration),
        );
        this.range = [this.range[0], (clampedEndAt / this.duration) * 100];
        this._endAt = parseEndAt(clampedEndAt, this.duration);
        this.clip.patchValue({ endAt: clampedEndAt });
      } else {
        this._endAt = value;
      }
    } catch {
      this._endAt = value;
    }
  }

  get duration() {
    return this.clip.value.asset.metadata.duration;
  }

  range = [0, 0];

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly config: DynamicDialogConfig<VideoClipEditorData>,
    private readonly videoProjectFormService: VideoProjectFormService,
  ) {}

  ngOnInit() {
    this.init(this.clip.value);

    this.videoProjectFormService
      .$onClipChanges(this.clip)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([_, value]) => {
        if (value) {
          this.init(value);
        }
      });
  }

  private init(value: VideoClipFormValue) {
    const startAt = value.startAt === -1 ? 0 : value.startAt;
    const endAt = value.endAt === -1 ? this.duration : value.endAt;

    this._startAt = parseStartAt(startAt);
    this._endAt = parseEndAt(endAt, this.duration);

    this.range = [
      (startAt / this.duration) * 100,
      (endAt / this.duration) * 100,
    ];
  }

  onRangeChange(value: [number, number]) {
    const min = Math.min(value[0], value[1]);
    const max = Math.max(value[0], value[1]);

    this.range = [min, max];

    const newStartAt = (min / 100) * this.duration;
    const newEndAt = (max / 100) * this.duration;

    this._startAt = parseStartAt(newStartAt);
    this._endAt = parseEndAt(newEndAt, this.duration);
    this.clip.patchValue({ startAt: newStartAt, endAt: newEndAt });
  }
}

@Component({
  selector: 'app-video-clip-editor-footer',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  template: `
    <div class="flex items-center space-x-2 mt-3">
      @if (config.data?.hasChanges()) {
        <p-button
          type="button"
          label="Reset"
          severity="warn"
          icon="pi pi-undo"
          (onClick)="this.ref.close('reset')" />
      }
      <p-button
        type="button"
        label="Done"
        icon="pi pi-check"
        (click)="this.ref.close()" />
    </div>
  `,
})
export class VideoClipEditorFooterComponent {
  constructor(
    readonly ref: DynamicDialogRef,
    readonly config: DynamicDialogConfig<VideoClipEditorData>,
  ) {}
}
